import type {
  RouteLocation,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import type { Acl, User } from "vue-simple-acl/types/acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { withPageNavigationMeta } from "~/core/utils/withPageNavigationMeta";
import {
  PATH_ORGANIZATIONS_ID,
  PATH_ORGANIZATIONS_ID_SETTINGS,
  PATH_ORGANIZATIONS_ID_SETTINGS_CERTIFICATE,
  PATH_ORGANIZATIONS_ID_SETTINGS_CERTIFICATE_TRUSTSTORE,
  PATH_ORGANIZATIONS_ID_SETTINGS_CERTIFICATE_TRUSTSTORE_ALIAS,
  PATH_ORGANIZATIONS_ID_SETTINGS_INFORMATION,
  PATH_ORGANIZATIONS_ID_SETTINGS_LICENSE,
  PATH_ORGANIZATIONS_ID_SETTINGS_LOGS,
  PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS,
  PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
  PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_DELEGATIONS,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_DELEGATION,
  PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_INFORMATION,
} from "~/modules/organization/constants";
import { router } from "~/plugins/router";
import { useAppBack } from "~/core/composables/useAppBack";

const { getBackToBusinessUnitsRoute } = useAppBack();

const routes: RouteRecordRaw[] = [
  {
    path: PATH_ORGANIZATIONS_ID,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_ORGANIZATIONS_ID_SETTINGS,
        params: { id: to.params.id },
      }),
  },
  {
    path: PATH_ORGANIZATIONS_ID_SETTINGS,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_ORGANIZATIONS_ID_SETTINGS_INFORMATION,
        params: { id: to.params.id },
      }),
  },
  {
    path: PATH_ORGANIZATIONS_ID_SETTINGS_CERTIFICATE,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_ORGANIZATIONS_ID_SETTINGS_CERTIFICATE_TRUSTSTORE,
        params: { id: to.params.id },
      }),
  },
  {
    path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS,
        params: { id: to.params.id },
      }),
  },
  {
    path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_INFORMATION,
        params: { id: to.params.id, userLogin: to.params.userLogin },
      }),
  },
  {
    path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
        params: { id: to.params.id, profileId: to.params.profileId },
      }),
  },
  // All the settings page routes share the same page identity: the
  // application back button ignores the tab navigations between them.
  ...withPageNavigationMeta(
    "organization-settings",
    getBackToBusinessUnitsRoute,
    [
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_CERTIFICATE_TRUSTSTORE,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_CERTIFICATE),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_CERTIFICATE_TRUSTSTORE_ALIAS,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_CERTIFICATE),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_INFORMATION,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_ORG),
        },
      },
      // Note: Do not use redirect here because the tab "Affiliation" can be not displayed.
      //       It will be replaced in the component RelationsComponent.
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_RELATION),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_RELATION),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_DELEGATIONS,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_RELATION),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_USER),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_LOGS,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_LICENSE,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_INFORMATION,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_WRITE_USER),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_DELEGATION,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_WRITE_USER),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_USER),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_USER),
        },
      },
      {
        path: PATH_ORGANIZATIONS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
        component: () =>
          import("~/modules/organization/views/OrganizationSettingsPage.vue"),
        meta: {
          layout: "AdministrationLayout",
          can: (
            _to: RouteLocationNormalized,
            _from: RouteLocationNormalized,
            can: Acl<User>["can"],
          ) => can(ACLRoles.CAN_READ_USER),
        },
      },
    ],
  ),
];

export default routes;
